import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import JsonLd from '@/components/base/JsonLd';
import { Link, useParams } from 'react-router-dom';
import { useEffect, Fragment } from 'react';
import { articles, guideAchatArticle } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hipobuyspreadsheets.lol';

function getAllArticles() {
  return [...articles, guideAchatArticle];
}

function renderContent(text: string) {
  if (!text.includes('hipobuy spreadsheet')) return text;
  const parts = text.split('hipobuy spreadsheet');
  return parts.map((part, i) => (
    <Fragment key={i}>
      {i > 0 && <strong className="text-foreground-900">hipobuy spreadsheet</strong>}
      {part}
    </Fragment>
  ));
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const allArticles = getAllArticles();
  const article = allArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 md:pt-28 pb-10">
          <div className="px-6 md:px-10 max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Article introuvable</h1>
            <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer">Retour à l'accueil</Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${SITE_URL}/article/${article.slug}#article`,
    headline: article.title,
    description: article.excerpt,
    url: `${SITE_URL}/article/${article.slug}`,
    inLanguage: 'fr',
    author: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet',
      url: SITE_URL,
    },
    datePublished: '2026-01-15T08:00:00+00:00',
    dateModified: '2026-06-23T08:00:00+00:00',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/article/${article.slug}`,
    },
  };

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd schema={articleJsonLd} />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords="hipobuy spreadsheet, guide hipobuy spreadsheet, comparatif spreadsheet, tutoriel hipobuy"
        canonical={`${SITE_URL}/article/${article.slug}`}
      />
      <Navbar />

      <article className="pt-24 md:pt-28 pb-10">
        <div className="px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <header className="mb-10">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-foreground-500 hover:text-accent-600 text-sm transition-colors mb-6 font-medium cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Retour à l'accueil
              </Link>

              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wide uppercase mb-4">
                {article.category}
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-foreground-500 text-xs">
                <span>Mis à jour : Juin 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.readTime} de lecture</span>
              </div>
            </header>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-56 md:h-72 object-cover object-top"
              />
            </div>

            <div className="space-y-8">
              {article.sections.map((section, idx) => {
                if (section.type === 'h2') {
                  return (
                    <h2 key={idx} className="font-heading text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {section.content}
                    </h2>
                  );
                }
                if (section.type === 'h3') {
                  return (
                    <h3 key={idx} className="font-heading text-lg font-semibold text-foreground-800 mt-6 mb-3">
                      {section.content}
                    </h3>
                  );
                }
                return (
                  <p key={idx} className="text-sm text-foreground-600 leading-relaxed">
                    {renderContent(section.content)}
                  </p>
                );
              })}
            </div>

            <section className="bg-accent-100/50 border border-accent-200/60 rounded-2xl p-6 md:p-8 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                Prêt à explorer le Hipobuy Spreadsheet ?
              </h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Accédez au site principal pour découvrir toutes les catégories et commencer dès aujourd'hui.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-accent-600 transition-all cursor-pointer"
                >
                  Hipobuy Spreadsheet
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full border border-foreground-200 text-foreground-700 font-medium text-sm whitespace-nowrap hover:border-foreground-400 transition-all cursor-pointer"
                >
                  Retour à l'accueil
                  <i className="ri-home-4-line ml-2"></i>
                </Link>
              </div>
            </section>

            <section className="mt-12 pt-8 border-t border-background-200">
              <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-4">Articles recommandés</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allArticles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="group flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={a.imageUrl} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground-800 group-hover:text-accent-600 transition-colors line-clamp-2">{a.title}</p>
                        <p className="text-xs text-foreground-400 mt-1">{a.readTime} de lecture</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}